import { useState } from 'react';
import { Link } from 'react-router-dom';

interface Vocabulary {
  term: string;
  definition: string;
}

interface Example {
  problem: string;
  steps: string[];
}

interface Question {
  id: string;
  question: string;
  options: string[];
  correctAnswer: number;
  explanation: string;
}

interface Lesson {
  id: string;
  title: string;
  unit: string;
  description: string;
  standards: string[];
  vocabulary: Vocabulary[];
  concepts: string[];
  examples: Example[];
  teacherTip: string;
  questions: Question[];
}

interface LessonCardProps {
  lesson: Lesson;
}

export default function LessonCard({ lesson }: LessonCardProps) {
  const [activeTab, setActiveTab] = useState<'overview' | 'practice'>('overview');
  const [selectedAnswers, setSelectedAnswers] = useState<Record<string, number>>({});
  const [showExplanations, setShowExplanations] = useState<Record<string, boolean>>({});
  const [submitted, setSubmitted] = useState(false);
  const [filter, setFilter] = useState<'all' | 'correct' | 'incorrect'>('all');
  const [expandedExample, setExpandedExample] = useState<number | null>(null);
  const [hoveredTerm, setHoveredTerm] = useState<string | null>(null);

  const handleAnswerSelect = (questionId: string, answerIndex: number) => {
    if (!submitted) {
      setSelectedAnswers(prev => ({ ...prev, [questionId]: answerIndex }));
    }
  };

  const handleSubmit = () => {
    setSubmitted(true);
  };

  const handleReset = () => {
    setSelectedAnswers({});
    setShowExplanations({});
    setSubmitted(false);
    setFilter('all');
  };

  const toggleExplanation = (questionId: string) => {
    setShowExplanations(prev => ({ ...prev, [questionId]: !prev[questionId] }));
  };

  const calculateScore = () => {
    const answered = Object.keys(selectedAnswers).length;
    const correct = lesson.questions.filter(
      q => selectedAnswers[q.id] === q.correctAnswer
    ).length;
    return { answered, correct, total: lesson.questions.length };
  };

  const filteredQuestions = lesson.questions.filter(q => {
    if (filter === 'all') return true;
    if (filter === 'correct') return selectedAnswers[q.id] === q.correctAnswer;
    if (filter === 'incorrect') return selectedAnswers[q.id] !== undefined && selectedAnswers[q.id] !== q.correctAnswer;
    return true;
  });

  const score = calculateScore();
  const percentage = score.answered > 0 ? Math.round((score.correct / score.answered) * 100) : 0;

  return (
    <div className="bg-white rounded-xl shadow-lg overflow-hidden border border-gray-100">
      {/* Header */}
      <div className="bg-gradient-to-r from-indigo-600 to-purple-600 p-6 text-white">
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <div className="text-sm font-medium opacity-90 mb-2">{lesson.unit}</div>
            <h3 className="text-2xl font-bold mb-2">{lesson.title}</h3>
            <p className="text-indigo-100 text-sm leading-relaxed">{lesson.description}</p>
          </div>
          <div className="ml-4">
            <div className="w-12 h-12 bg-white/20 rounded-lg flex items-center justify-center">
              <i className="ri-book-open-line text-2xl"></i>
            </div>
          </div>
        </div>
        
        {/* Standards */}
        <div className="mt-4 flex flex-wrap gap-2">
          {lesson.standards.map((standard, idx) => (
            <span key={idx} className="px-3 py-1 bg-white/20 rounded-full text-xs font-medium">
              {standard}
            </span>
          ))}
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200 bg-gray-50">
        <div className="flex">
          <button
            onClick={() => setActiveTab('overview')}
            className={`flex-1 px-6 py-4 text-sm font-semibold transition-colors ${
              activeTab === 'overview'
                ? 'text-indigo-600 border-b-2 border-indigo-600 bg-white'
                : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
            }`}
          >
            <i className="ri-file-list-3-line mr-2"></i>
            Lesson Overview
          </button>
          <button
            onClick={() => setActiveTab('practice')}
            className={`flex-1 px-6 py-4 text-sm font-semibold transition-colors ${
              activeTab === 'practice'
                ? 'text-indigo-600 border-b-2 border-indigo-600 bg-white'
                : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
            }`}
          >
            <i className="ri-question-answer-line mr-2"></i>
            Practice Questions ({lesson.questions.length})
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="p-6">
        {activeTab === 'overview' && (
          <div className="space-y-8">
            {/* Vocabulary */}
            <div>
              <h4 className="text-lg font-bold text-gray-900 mb-4 flex items-center">
                <i className="ri-book-2-line text-indigo-600 mr-2"></i>
                Key Vocabulary
              </h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {lesson.vocabulary.map((vocab, idx) => (
                  <div
                    key={idx}
                    className="relative p-4 bg-gradient-to-br from-indigo-50 to-purple-50 rounded-lg border border-indigo-100 cursor-help"
                    onMouseEnter={() => setHoveredTerm(vocab.term)}
                    onMouseLeave={() => setHoveredTerm(null)}
                  >
                    <div className="font-semibold text-indigo-900">{vocab.term}</div>
                    {hoveredTerm === vocab.term && (
                      <div className="absolute z-10 left-0 right-0 top-full mt-2 p-3 bg-gray-900 text-white text-sm rounded-lg shadow-xl">
                        {vocab.definition}
                        <div className="absolute -top-2 left-4 w-4 h-4 bg-gray-900 transform rotate-45"></div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* Key Concepts */}
            <div>
              <h4 className="text-lg font-bold text-gray-900 mb-4 flex items-center">
                <i className="ri-lightbulb-line text-yellow-500 mr-2"></i>
                Key Concepts
              </h4>
              <ul className="space-y-3">
                {lesson.concepts.map((concept, idx) => (
                  <li key={idx} className="flex items-start">
                    <span className="flex-shrink-0 w-6 h-6 bg-indigo-100 text-indigo-600 rounded-full flex items-center justify-center text-xs font-bold mr-3 mt-0.5">
                      {idx + 1}
                    </span>
                    <span className="text-gray-700 leading-relaxed">{concept}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Worked Examples */}
            <div>
              <h4 className="text-lg font-bold text-gray-900 mb-4 flex items-center">
                <i className="ri-calculator-line text-green-600 mr-2"></i>
                Worked Examples
              </h4>
              <div className="space-y-4">
                {lesson.examples.map((example, idx) => (
                  <div key={idx} className="border border-gray-200 rounded-lg overflow-hidden">
                    <button
                      onClick={() => setExpandedExample(expandedExample === idx ? null : idx)}
                      className="w-full px-5 py-4 bg-gradient-to-r from-green-50 to-teal-50 text-left font-semibold text-gray-900 hover:from-green-100 hover:to-teal-100 transition-colors flex items-center justify-between"
                    >
                      <span className="flex items-center">
                        <span className="w-8 h-8 bg-green-600 text-white rounded-full flex items-center justify-center text-sm font-bold mr-3">
                          {idx + 1}
                        </span>
                        {example.problem}
                      </span>
                      <i className={`ri-arrow-${expandedExample === idx ? 'up' : 'down'}-s-line text-xl text-gray-600`}></i>
                    </button>
                    {expandedExample === idx && (
                      <div className="p-5 bg-white space-y-3">
                        {example.steps.map((step, stepIdx) => (
                          <div key={stepIdx} className="flex items-start">
                            <span className="flex-shrink-0 w-7 h-7 bg-gradient-to-br from-green-500 to-teal-500 text-white rounded-full flex items-center justify-center text-xs font-bold mr-3 mt-0.5">
                              {stepIdx + 1}
                            </span>
                            <span className="text-gray-700 leading-relaxed font-mono text-sm bg-gray-50 px-3 py-2 rounded flex-1">
                              {step}
                            </span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* Teacher Tip */}
            <div className="bg-gradient-to-r from-amber-50 to-orange-50 border-l-4 border-amber-500 p-5 rounded-r-lg">
              <div className="flex items-start">
                <i className="ri-lightbulb-flash-line text-2xl text-amber-600 mr-3 mt-1"></i>
                <div>
                  <h4 className="font-bold text-amber-900 mb-2">Mr. Augustine's Teaching Tip</h4>
                  <p className="text-amber-800 leading-relaxed">{lesson.teacherTip}</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'practice' && (
          <div>
            {/* Progress Bar */}
            {submitted && (
              <div className="mb-6 p-5 bg-gradient-to-r from-indigo-50 to-purple-50 rounded-xl border border-indigo-200">
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <div className="text-2xl font-bold text-indigo-900">
                      {score.correct} / {score.answered} Correct
                    </div>
                    <div className="text-sm text-indigo-600 font-medium">
                      {percentage}% Score
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-3xl font-bold text-indigo-600">{percentage}%</div>
                    <div className="text-xs text-gray-600">Performance</div>
                  </div>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-3 overflow-hidden">
                  <div
                    className={`h-full rounded-full transition-all duration-500 ${
                      percentage >= 80 ? 'bg-gradient-to-r from-green-500 to-emerald-500' :
                      percentage >= 60 ? 'bg-gradient-to-r from-yellow-500 to-orange-500' :
                      'bg-gradient-to-r from-red-500 to-pink-500'
                    }`}
                    style={{ width: `${percentage}%` }}
                  ></div>
                </div>
              </div>
            )}

            {/* Controls */}
            <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setFilter('all')}
                  className={`px-4 py-2 rounded-lg text-sm font-semibold transition-colors ${
                    filter === 'all'
                      ? 'bg-indigo-600 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  All ({lesson.questions.length})
                </button>
                {submitted && (
                  <>
                    <button
                      onClick={() => setFilter('correct')}
                      className={`px-4 py-2 rounded-lg text-sm font-semibold transition-colors ${
                        filter === 'correct'
                          ? 'bg-green-600 text-white'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      Correct ({score.correct})
                    </button>
                    <button
                      onClick={() => setFilter('incorrect')}
                      className={`px-4 py-2 rounded-lg text-sm font-semibold transition-colors ${
                        filter === 'incorrect'
                          ? 'bg-red-600 text-white'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      Incorrect ({score.answered - score.correct})
                    </button>
                  </>
                )}
              </div>
              <div className="flex gap-2">
                {!submitted ? (
                  <button
                    onClick={handleSubmit}
                    disabled={Object.keys(selectedAnswers).length === 0}
                    className="px-6 py-2 bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-lg font-semibold hover:from-indigo-700 hover:to-purple-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-lg hover:shadow-xl"
                  >
                    <i className="ri-check-line mr-2"></i>
                    Submit Answers
                  </button>
                ) : (
                  <button
                    onClick={handleReset}
                    className="px-6 py-2 bg-gradient-to-r from-gray-600 to-gray-700 text-white rounded-lg font-semibold hover:from-gray-700 hover:to-gray-800 transition-all shadow-lg hover:shadow-xl"
                  >
                    <i className="ri-refresh-line mr-2"></i>
                    Try Again
                  </button>
                )}
              </div>
            </div>

            {/* Questions */}
            <div className="space-y-6">
              {filteredQuestions.map((question, idx) => {
                const isAnswered = selectedAnswers[question.id] !== undefined;
                const isCorrect = selectedAnswers[question.id] === question.correctAnswer;
                const showResult = submitted && isAnswered;

                return (
                  <div
                    key={question.id}
                    className={`border-2 rounded-xl p-5 transition-all ${
                      showResult
                        ? isCorrect
                          ? 'border-green-300 bg-green-50'
                          : 'border-red-300 bg-red-50'
                        : 'border-gray-200 bg-white hover:border-indigo-300'
                    }`}
                  >
                    <div className="flex items-start mb-4">
                      <span className="flex-shrink-0 w-8 h-8 bg-indigo-600 text-white rounded-full flex items-center justify-center text-sm font-bold mr-3">
                        {lesson.questions.indexOf(question) + 1}
                      </span>
                      <div className="flex-1">
                        <p className="text-gray-900 font-medium leading-relaxed" dangerouslySetInnerHTML={{ __html: question.question }}></p>
                      </div>
                      {showResult && (
                        <div className={`ml-3 flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center ${
                          isCorrect ? 'bg-green-500' : 'bg-red-500'
                        }`}>
                          <i className={`ri-${isCorrect ? 'check' : 'close'}-line text-white text-lg`}></i>
                        </div>
                      )}
                    </div>

                    <div className="space-y-2 ml-11">
                      {question.options.map((option, optionIdx) => {
                        const isSelected = selectedAnswers[question.id] === optionIdx;
                        const isCorrectAnswer = optionIdx === question.correctAnswer;
                        const showCorrectAnswer = submitted && isCorrectAnswer;

                        return (
                          <button
                            key={optionIdx}
                            onClick={() => handleAnswerSelect(question.id, optionIdx)}
                            disabled={submitted}
                            className={`w-full text-left px-4 py-3 rounded-lg border-2 transition-all ${
                              submitted
                                ? showCorrectAnswer
                                  ? 'border-green-500 bg-green-100 font-semibold'
                                  : isSelected && !isCorrect
                                  ? 'border-red-500 bg-red-100'
                                  : 'border-gray-200 bg-gray-50'
                                : isSelected
                                ? 'border-indigo-500 bg-indigo-50 font-semibold'
                                : 'border-gray-200 bg-white hover:border-indigo-300 hover:bg-indigo-50'
                            } ${submitted ? 'cursor-default' : 'cursor-pointer'}`}
                          >
                            <div className="flex items-center">
                              <span className={`flex-shrink-0 w-6 h-6 rounded-full border-2 flex items-center justify-center mr-3 text-xs font-bold ${
                                submitted
                                  ? showCorrectAnswer
                                    ? 'border-green-600 bg-green-600 text-white'
                                    : isSelected && !isCorrect
                                    ? 'border-red-600 bg-red-600 text-white'
                                    : 'border-gray-300 bg-white text-gray-600'
                                  : isSelected
                                  ? 'border-indigo-600 bg-indigo-600 text-white'
                                  : 'border-gray-300 bg-white text-gray-600'
                              }`}>
                                {String.fromCharCode(65 + optionIdx)}
                              </span>
                              <span dangerouslySetInnerHTML={{ __html: option }}></span>
                            </div>
                          </button>
                        );
                      })}
                    </div>

                    {submitted && isAnswered && (
                      <div className="mt-4 ml-11">
                        <button
                          onClick={() => toggleExplanation(question.id)}
                          className="text-sm font-semibold text-indigo-600 hover:text-indigo-700 flex items-center"
                        >
                          <i className={`ri-${showExplanations[question.id] ? 'eye-off' : 'eye'}-line mr-1`}></i>
                          {showExplanations[question.id] ? 'Hide' : 'Show'} Explanation
                        </button>
                        {showExplanations[question.id] && (
                          <div className="mt-3 p-4 bg-white border-l-4 border-indigo-500 rounded-r-lg">
                            <div className="flex items-start">
                              <i className="ri-information-line text-indigo-600 text-lg mr-2 mt-0.5"></i>
                              <p className="text-gray-700 text-sm leading-relaxed">{question.explanation}</p>
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}